import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, getRedirectResult, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GUEST_KEY = 'zindal_guest_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Capture result from signInWithRedirect (Google on mobile)
    getRedirectResult(auth).catch((err) => {
      if (err?.code && err.code !== 'auth/no-redirect-operation') {
        console.error('Redirect sign-in error:', err?.code, err?.message);
      }
    });

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Logged in via Google → clear any guest session
        try { localStorage.removeItem(GUEST_KEY); } catch {}
        const fallbackName = firebaseUser.email?.split('@')[0] || 'User';
        const userData = {
          uid: firebaseUser.uid,
          name: firebaseUser.displayName || fallbackName,
          email: firebaseUser.email,
          provider: firebaseUser.providerData[0]?.providerId || 'google',
          isGuest: false,
        };
        try {
          const res = await axios.post(`${API}/auth/user`, userData);
          const serverName = res?.data?.name;
          if (serverName && serverName !== fallbackName) userData.name = serverName;
        } catch (error) {
          console.error('Error syncing user:', error);
        }
        setUser(userData);
      } else {
        // Restore guest session if exists
        try {
          const guest = localStorage.getItem(GUEST_KEY);
          if (guest) {
            setUser(JSON.parse(guest));
          } else {
            setUser(null);
          }
        } catch {
          setUser(null);
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const loginWithGoogle = async () => {
    return await signInWithPopup(auth, googleProvider);
  };

  const loginAsGuest = async () => {
    const guestId = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const guestUser = {
      uid: guestId,
      name: 'Guest',
      email: null,
      provider: 'guest',
      isGuest: true,
    };
    try { localStorage.setItem(GUEST_KEY, JSON.stringify(guestUser)); } catch {}
    setUser(guestUser);
    return guestUser;
  };

  const logout = async () => {
    try { localStorage.removeItem(GUEST_KEY); } catch {}
    if (auth.currentUser) {
      await signOut(auth);
    }
    setUser(null);
  };

  const value = {
    user,
    loading,
    loginWithGoogle,
    loginAsGuest,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
